import React, { useState, useCallback, useRef } from 'react';
import { Search, Loader2, Pill, X } from 'lucide-react';
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { searchMedications } from '../../services/api';
import type { FacetOption, FdbMedicationResult } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';

interface MedicationSearchProps {
  // Only `full_name` is read for display, so accept any selected-medication
  // shape (a fresh FDB result or an already-linked local medication).
  value?: { full_name: string } | null;
  onSelect?: (medication: FdbMedicationResult) => void;
  onClear?: () => void;
  onAddOffMarket?: (searchTerm: string) => void;
  placeholder?: string;
}

// Page size for the initial fetch and each "Show more" increment.
const PAGE_SIZE = 10;
// The backend caps its rerank fetch window (MAX_FETCH_WINDOW); results beyond
// this are not paginable, so stop offering "Show more" there.
const MAX_RESULTS = 200;

const highlightMatch = (text: string, query: string): React.ReactNode => {
  if (!query || query.length < 3) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return (
    <>
      {before}
      <span className='font-bold'>{match}</span>
      {after}
    </>
  );
};

const MedicationSearch: React.FC<MedicationSearchProps> = ({
  value,
  onSelect,
  onClear,
  onAddOffMarket,
  placeholder = 'Search medications...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<FdbMedicationResult[]>([]);
  const [searchError, setSearchError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // If there's a selected value, show it in the input
  const displayValue = value ? value.full_name : searchTerm;

  // Filter state
  const [selectedForm, setSelectedForm] = useState<string>('');
  const [selectedRoute, setSelectedRoute] = useState<string>('');
  const [availableForms, setAvailableForms] = useState<FacetOption[]>([]);
  const [availableRoutes, setAvailableRoutes] = useState<FacetOption[]>([]);

  const performSearch = useCallback(
    async (
      term: string,
      form?: string,
      route?: string,
      fetchLimit: number = PAGE_SIZE
    ) => {
      if (!term || term.length < 3) {
        setResults([]);
        setTotalCount(0);
        setSearchError(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await searchMedications({
          query: {
            search: term,
            limit: fetchLimit,
            form: form || undefined,
            route: route || undefined,
          },
        });

        // The client is configured without `throwOnError`, so HTTP failures
        // (incl. the now-external FDB proxy) resolve with a populated `error`
        // rather than throwing. Handle them explicitly, otherwise stale results
        // would linger and misleadingly offer "Add Off Market".
        if (response.error || !response.data) {
          console.error('Error searching medications:', response.error);
          setSearchError(true);
          setResults([]);
          setTotalCount(0);
          setAvailableForms([]);
          setAvailableRoutes([]);
          return;
        }

        setSearchError(false);
        setResults(response.data.items);
        setTotalCount(response.data.total);
        setAvailableForms(response.data.available_forms ?? []);
        setAvailableRoutes(response.data.available_routes ?? []);
      } catch (error) {
        console.error('Error searching medications:', error);
        setSearchError(true);
        setResults([]);
        setTotalCount(0);
        setAvailableForms([]);
        setAvailableRoutes([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const debouncedSearch = useDebounce(
    (term: string, form: string, route: string) =>
      performSearch(term, form, route),
    300
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length >= 3) {
      setOpen(true);
      setIsLoading(true);
      setLimit(PAGE_SIZE);
      debouncedSearch(value, selectedForm, selectedRoute);
    } else {
      setResults([]);
      setTotalCount(0);
      setOpen(false);
      // Clear filters when search is cleared
      setSelectedForm('');
      setSelectedRoute('');
      setAvailableForms([]);
      setAvailableRoutes([]);
    }
  };

  const clearFilters = () => {
    setSelectedForm('');
    setSelectedRoute('');
    setLimit(PAGE_SIZE);
    if (searchTerm.length >= 3) {
      setIsLoading(true);
      performSearch(searchTerm, '', '');
    }
  };

  const hasActiveFilters = selectedForm !== '' || selectedRoute !== '';

  const handleFormChange = (value: string) => {
    const newForm = value === 'all' ? '' : value;
    setSelectedForm(newForm);
    setLimit(PAGE_SIZE);
    if (searchTerm.length >= 3) {
      setIsLoading(true);
      performSearch(searchTerm, newForm, selectedRoute);
    }
  };

  const handleRouteChange = (value: string) => {
    const newRoute = value === 'all' ? '' : value;
    setSelectedRoute(newRoute);
    setLimit(PAGE_SIZE);
    if (searchTerm.length >= 3) {
      setIsLoading(true);
      performSearch(searchTerm, selectedForm, newRoute);
    }
  };

  const handleShowMore = () => {
    const nextLimit = Math.min(limit + PAGE_SIZE, MAX_RESULTS);
    setLimit(nextLimit);
    setIsLoading(true);
    performSearch(searchTerm, selectedForm, selectedRoute, nextLimit);
  };

  const canShowMore =
    !searchError && results.length < totalCount && results.length < MAX_RESULTS;

  const handleSelect = (medication: FdbMedicationResult) => {
    if (onSelect) {
      onSelect(medication);
    }
    setSearchTerm('');
    setResults([]);
    setOpen(false);
  };

  const handleFocus = () => {
    // Don't open dropdown if a medication is selected
    if (!value && searchTerm.length >= 3) {
      setOpen(true);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setResults([]);
    setOpen(false);
    if (onClear) {
      onClear();
    }
    // Focus the input after clearing
    inputRef.current?.focus();
  };

  const showDropdown = open && searchTerm.length >= 3 && !value;

  return (
    // modal: the picker renders inside a modal Dialog whose scroll-lock blocks
    // wheel events in portalled content; a modal popover registers its own
    // scroll shard so the results list stays wheel-scrollable.
    <Popover open={showDropdown} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <div className='relative w-full'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10' />
          <Input
            ref={inputRef}
            type='text'
            placeholder={placeholder}
            value={displayValue}
            onChange={e => {
              // If there's a selected value and user starts typing, clear it first
              if (value && onClear) {
                onClear();
              }
              handleInputChange(e);
            }}
            onFocus={handleFocus}
            className='pl-9 pr-9'
            inputSize='default'
          />
          {isLoading && !value && (
            // Positioning lives on the wrapper: animate-spin's keyframes
            // overwrite the element's whole transform, so -translate-y on the
            // spinner itself makes it jitter.
            <span className='absolute right-3 top-1/2 -translate-y-1/2'>
              <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
            </span>
          )}
          {value && (
            <Button
              variant='ghost'
              size='sm'
              className='absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0'
              onClick={handleClear}
            >
              <X className='h-4 w-4 text-muted-foreground' />
            </Button>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        // Wider than the trigger (viewport permitting) so long medication
        // names stay readable; never narrower than the search box.
        className='p-0 min-w-[var(--radix-popover-trigger-width)] w-[min(40rem,var(--radix-popover-content-available-width))]'
        align='start'
        sideOffset={4}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        {results.length > 0 ? (
          <ul className='max-h-[300px] overflow-auto py-1'>
            {results.map(medication => (
              <li key={medication.fdb_dispensable_drug_id}>
                <button
                  type='button'
                  className='w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none'
                  onClick={() => handleSelect(medication)}
                >
                  <div className='flex items-start gap-2'>
                    <Pill className='mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground' />
                    <div className='min-w-0 flex-1'>
                      <div className='text-sm break-words'>
                        {highlightMatch(medication.full_name, searchTerm)}
                      </div>
                      <div className='text-xs text-muted-foreground flex flex-wrap gap-x-2'>
                        {medication.generic_name && (
                          <span>
                            {highlightMatch(
                              medication.generic_name,
                              searchTerm
                            )}
                          </span>
                        )}
                        {medication.therapeutic_category && (
                          <span className='text-muted-foreground/70'>
                            {medication.therapeutic_category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : searchError ? (
          <div className='px-3 py-6 text-center text-sm text-muted-foreground'>
            <p>Search failed. Please try again.</p>
          </div>
        ) : !isLoading ? (
          <div className='px-3 py-6 text-center text-sm text-muted-foreground'>
            <p>No medications found for "{searchTerm}"</p>
            {hasActiveFilters && (
              <Button
                variant='outline'
                size='sm'
                className='mt-2 mx-auto block'
                onClick={clearFilters}
              >
                Clear Filters
              </Button>
            )}
            {onAddOffMarket && (
              <Button
                variant='outline'
                size='sm'
                className='mt-2 mx-auto block'
                onClick={() => {
                  onAddOffMarket(searchTerm);
                  setSearchTerm('');
                  setOpen(false);
                }}
              >
                Add Off Market / Unlisted Medication
              </Button>
            )}
          </div>
        ) : null}

        {/* Filters + pagination - persist during refetches, greyed out */}
        <div
          className={`border-t px-3 py-2 ${
            isLoading ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          <div className='flex items-center justify-between gap-2'>
            <span className='flex items-center gap-1.5 text-xs text-muted-foreground'>
              {isLoading && <Loader2 className='h-3 w-3 animate-spin' />}
              {results.length > 0
                ? `Showing ${results.length} of ${totalCount} results`
                : 'Filter results'}
              {results.length > 0 && canShowMore && (
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-6 px-2 text-xs'
                  onClick={handleShowMore}
                >
                  Show more
                </Button>
              )}
            </span>
            <div className='flex items-center gap-2'>
              <div className='flex items-center gap-1'>
                <Select
                  value={selectedForm || 'all'}
                  onValueChange={handleFormChange}
                >
                  <SelectTrigger
                    className='h-7 w-24 text-xs'
                    onClick={e => e.stopPropagation()}
                  >
                    <SelectValue placeholder='Form' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All forms</SelectItem>
                    {availableForms.map(form => (
                      <SelectItem key={form.id} value={form.id}>
                        {form.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedForm && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-5 w-5 p-0'
                    onClick={e => {
                      e.stopPropagation();
                      handleFormChange('all');
                    }}
                  >
                    <X className='h-3 w-3 text-muted-foreground' />
                  </Button>
                )}
              </div>
              <div className='flex items-center gap-1'>
                <Select
                  value={selectedRoute || 'all'}
                  onValueChange={handleRouteChange}
                >
                  <SelectTrigger
                    className='h-7 w-24 text-xs'
                    onClick={e => e.stopPropagation()}
                  >
                    <SelectValue placeholder='Route' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All routes</SelectItem>
                    {availableRoutes.map(route => (
                      <SelectItem key={route.id} value={route.id}>
                        {route.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedRoute && (
                  <Button
                    variant='ghost'
                    size='sm'
                    className='h-5 w-5 p-0'
                    onClick={e => {
                      e.stopPropagation();
                      handleRouteChange('all');
                    }}
                  >
                    <X className='h-3 w-3 text-muted-foreground' />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MedicationSearch;
